import React, { FC, useState } from 'react';
import { Container } from '@design-system/container';
import { Typography } from '@design-system/typography';

import {
  customProductVariantsSchema,
  ICustomProductVariantsProps,
} from './CustomProductVariants.schema';

import './CustomProductVariants.scss';

const BLOCK = 'custom-product';

type DimensionKey = 'size' | 'scent';

interface IProductImage {
  imageUrl?: string;
  imageAlt?: string;
}

interface IRetailer {
  retailerUrl: string;
  retailerImage: string;
  retailerTitle?: string;
}

interface IDimension {
  key: DimensionKey;
  label: string;
  options: string[];
  activeValue: string;
}

const parseJson = (value: string | undefined) => {
  if (!value?.trim()) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const parseStringArray = (value: string | undefined): string[] => {
  const parsed = parseJson(value);

  return Array.isArray(parsed) ? parsed.map(asString) : [];
};

const normalizeImage = (value: unknown): IProductImage => {
  if (typeof value === 'string') {
    return { imageUrl: value };
  }

  if (value && typeof value === 'object') {
    const raw = value as IProductImage;

    return { imageUrl: raw.imageUrl, imageAlt: raw.imageAlt };
  }

  return {};
};

const parseImageArray = (value: string | undefined): IProductImage[] => {
  const parsed = parseJson(value);

  return Array.isArray(parsed) ? parsed.map(normalizeImage) : [];
};

const normalizeRetailers = (value: unknown): IRetailer[] =>
  Array.isArray(value)
    ? (value as IRetailer[]).filter((retailer) => retailer?.retailerUrl || retailer?.retailerImage)
    : [];

const parseRetailerMatrix = (value: string | undefined): IRetailer[][] => {
  const parsed = parseJson(value);

  if (!Array.isArray(parsed)) {
    return [];
  }

  if (parsed.length && !Array.isArray(parsed[0])) {
    return [normalizeRetailers(parsed)];
  }

  return parsed.map(normalizeRetailers);
};

const uniqueValues = (values: string[]): string[] =>
  values.filter((value, index) => value && values.indexOf(value) === index);

const renderDimension = (dimension: IDimension, onSelect: (value: string) => void) => (
  <div className="cds-variant-selector__block" key={dimension.key}>
    {dimension.label ? (
      <Typography content={dimension.label} variant="body" size="s" tag="p" />
    ) : null}
    <ul className="cds-variant-selector__list">
      {dimension.options.map((value) => {
        const isActive = value === dimension.activeValue;

        return (
          <li key={value}>
            <button
              type="button"
              className={`cds-selector-button cds-selector-button__btn${
                isActive ? ' selected' : ''
              }`}
              aria-current={isActive ? true : undefined}
              onClick={() => onSelect(value)}
            >
              <span className="cds-selector-button__inner">{value}</span>
            </button>
          </li>
        );
      })}
    </ul>
  </div>
);

const renderRetailer = (retailer: IRetailer, index: number) => {
  // Retailers have no unique id and a shop URL can repeat within a product, so the list index
  // keeps each key unique. The per-product list is static (never reordered), so this is safe.
  const itemKey = `${retailer.retailerUrl || retailer.retailerImage}-${index}`;

  return (
    <li className="cds-retailers__item" key={itemKey}>
      <a
        className="cds-link"
        href={retailer.retailerUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={retailer.retailerTitle ?? ''}
      >
        <img
          className={`${BLOCK}__retailer-image`}
          src={retailer.retailerImage}
          alt={retailer.retailerTitle ?? ''}
        />
      </a>
    </li>
  );
};

const CustomProductVariants: FC<ICustomProductVariantsProps> = (props) => {
  const { value: validated } = customProductVariantsSchema.validate(props, {
    abortEarly: false,
    stripUnknown: true,
  });

  const data = validated as ICustomProductVariantsProps;

  const titles = parseStringArray(data.titles);
  const descriptions = parseStringArray(data.descriptions);
  const images = parseImageArray(data.images);
  const sizes = parseStringArray(data.sizes);
  const scents = parseStringArray(data.scents);
  const retailerMatrix = parseRetailerMatrix(data.retailers);

  const [activeIndex, setActiveIndex] = useState(0);

  if (!titles.length) {
    return null;
  }

  const active = activeIndex < titles.length ? activeIndex : 0;

  const selectByDimension = (dimension: DimensionKey, value: string): void => {
    const primary = dimension === 'size' ? sizes : scents;
    const other = dimension === 'size' ? scents : sizes;
    const matchedIndex = primary.findIndex(
      (item, index) => item === value && other[index] === other[active]
    );
    const fallbackIndex = primary.findIndex((item) => item === value);
    const nextIndex = matchedIndex === -1 ? fallbackIndex : matchedIndex;

    if (nextIndex !== -1) {
      setActiveIndex(nextIndex);
    }
  };

  const activeImage = images[active] ?? {};
  const activeRetailers = retailerMatrix[active] ?? retailerMatrix[0] ?? [];

  const dimensions: IDimension[] = [
    {
      key: 'size' as const,
      label: data.sizeLabel,
      options: uniqueValues(sizes),
      activeValue: sizes[active],
    },
    {
      key: 'scent' as const,
      label: data.scentLabel,
      options: uniqueValues(scents),
      activeValue: scents[active],
    },
  ].filter((dimension) => dimension.options.length);

  return (
    <div className={`cds-section cds-product-page-header ${BLOCK}`}>
      <Container maxWidth="max-grid" className="cds-product-page-header__container">
        <div className={`${BLOCK}__media`}>
          {activeImage.imageUrl ? (
            <img src={activeImage.imageUrl} alt={activeImage.imageAlt ?? ''} />
          ) : null}
        </div>

        <div className="cds-product-page-header__variants">
          {titles[active] ? (
            <h2 className="cds-product-page-header__title">
              <Typography
                content={titles[active]}
                weight="bold"
                variant="headline"
                size="xl"
                tag="span"
              />
            </h2>
          ) : null}

          {descriptions[active] ? (
            <Typography
              className="cds-product-page-header__description"
              content={descriptions[active]}
              variant="body"
              size="m"
              tag="p"
            />
          ) : null}

          {dimensions.length ? (
            <div className="cds-variant-selector">
              {dimensions.map((dimension) =>
                renderDimension(dimension, (value) => selectByDimension(dimension.key, value))
              )}
            </div>
          ) : null}

          {activeRetailers.length ? (
            <div className="cds-retailers">
              {data.retailersTitle ? (
                <Typography
                  className="cds-retailers__title"
                  content={data.retailersTitle}
                  variant="body"
                  size="m"
                  tag="p"
                />
              ) : null}
              <ul className="cds-retailers__items">{activeRetailers.map(renderRetailer)}</ul>
            </div>
          ) : null}
        </div>
      </Container>
    </div>
  );
};

export default CustomProductVariants;
